import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import WebViewer from '@pdftron/webviewer';
import { initializeVideoViewer } from '@pdftron/webviewer-video/dist/main-with-react';
import { displayToast } from '../app/app-slice';

const settingsIcon = '/settings.svg';
const fullScreenIcon = '/full-screen.svg';

const PDFTRON_LIB_PATH = '/pdftron-12.0.1';

const PDFTRON_WEBVIWER_LICENSE =
  'ZenQMS LLC:OEM:ZenQMS::B+:AMS(20270629):8AB63B63B68CAD5889994E616F7F0AF5D3F14FAF25EECFCF1CD24AB239F5C7';

const PDFTRON_WEBVIWER_VIDEO_LICENSE =
  'alin@zenqms.com///114:173:86:209:70:153:135:195:0:246:98:33:42:212:199:251:165:13:119:254:118:241:90:3:164:133:228:185:132:150:111:30:3:54:109:252:34:42:3:110:232:195:249:131:174:39:159:110:209:136:255:125:17:27:109:153:208:29:149:6:25:82:41:141:239:45:148:250:89:105:28:227:201:190:79:153:161:167:35:18:155:72:12:84:29:198:85:150:215:10:230:191:104:189:30:130:123:55:187:186:107:192:190:69:130:232:229:159:255:198:176:222:118:67:241:178:30:218:73:51:240:92:214:185:95:169:222:78:132:147:29:199:34:73:29:57:195:99:48:233:254:25:137:200:63:159:240:91:1:60:52:108:123:49:11:0:162:55:120:14:178:166:72:91:129:123:99:193:9:3:133:78:202:62:200:63:231:225:43:35:181:168:228:69:180:147:215:103:224:43:201:200:95:124:184:112:172:128:134:205:32:12:76:73:81:79:174:84:135:227:108:198:203:33:80:97:6:37:110:50:160:118:165:28:128:50:233:171:177:92:217:155:147:23:108:70:64:185:57:78:235:171:185:91:184:111:115:57:239:120:72:183:50:95:77:115:217:206:192:164:127:190:113:129:152:63:212:16:49:89:55:17:165:23:173:24:140:135:164:160:233:175:132:0:12:210:186:89:87:17:168:86:121:49:114:53:163:87:154:23:104:87:167:201:48:72:49:246:105:58:24:55:250:189:9:164:87:121:57:22:67:97:226:185:165:221:7:218:20:186:78:248:100:176:186:209:60:1:118:103:218:108:147:0:20:246:129:234:80:26:134:61:23:200:243:248:10:86:247:140:139:122:149:136:138:254:22:181:132:200:13:207:241:165:33:197:105:81:250:247:214:52:119:65:133:74:3:167:204:85:254:235:181:45:3:120:239:117:91:174:82:131:161:107:108:98:171:17:48:250:145:238:149:112:222:41:0:143:235:66:8:39:98:124:159:203:44:75:37:217:152:116:170:115:182:146:123:137:200:76:58:10:24:111:219:0:35:83:50:171:107:152:80:16:120:147:158:55:27:178:189:100:125:96:1:235:77:22:66:203:47:101:139:89:153:68:47:198:242:53:235:36:30:24:82:122:47:142:214:189:252:200:140:128:211:45:86:107:219:248:185:70:12:150:15:218:153:79:252:55:164:93';

const DEFAULT_DISABLED_ELEMENTS = [
  'menuButton',
  'downloadButton',
  'saveAsButton',
  'toggleNotesButton',
  'panToolButton',
  'ribbons',
  'thumbDelete',
  'pageManipulationOverlayButton',
  'settingsAdvancedButton',
  'settingsKeyboardButton',
  'settingsLanguageSection',
  'thumbMultiSelect',
  'bookmarksPanel-tabPanel',
  'layersPanel-tabPanel',
  'fileAttachmentPanel-tabPanel',
  'default-ribbon-group',
  'notesPanelToggle',
  'deleteSelectedColor',
  'notesPanel',
  'annotationEditToolButton',
  'tools-header',
  'linkAnnotationUnlinkButton',
];

const WATERMARK_DEFAULT_OPTIONS = {
  fontSize: 24,
  color: 'red',
  opacity: 30,
};

const VIDEO_FORMATS = ['mp4', 'ogg', 'webm'];

// Passed to loadVideo so webviewer-video does not probe the real frame count. Without an fps it
// opens a WebSocket to Apryse's hosted ffprobe service and sends it the video url — here a
// presigned S3 url carrying temporary AWS credentials. ZenSign only plays videos (no frame-accurate
// annotating), so an assumed frame rate is enough.
const VIDEO_FPS = 30;

/**
 * Maps a watermark position to the zone PDFTron draws it in. `left` and `top` share the header,
 * `bottom` is the footer, and anything else (`center`) runs diagonally across the video.
 */
function buildWatermarkOptions(message, position) {
  const options = {
    shouldDrawOverAnnotations: true,
  };

  switch (position) {
    case 'left':
    case 'top':
      options.header = {
        ...WATERMARK_DEFAULT_OPTIONS,
        [position === 'left' ? 'left' : 'center']: message,
      };
      break;
    case 'bottom':
      options.footer = {
        ...WATERMARK_DEFAULT_OPTIONS,
        center: message,
      };
      break;
    default:
      options.diagonal = {
        ...WATERMARK_DEFAULT_OPTIONS,
        text: message,
      };
      break;
  }

  return options;
}

// A video skips PDFizer, so nothing burns its watermark in: the viewer draws it. Callers must only
// call this once the video stream is established — setWatermark re-reads the video, and doing so
// while loadVideo is still creating the stream caused a Shaka error in nova.
function drawVideoWatermark(instance, watermarkOptions) {
  if (!watermarkOptions) return;
  const { message, position } = watermarkOptions;
  instance.Core.documentViewer.setWatermark(buildWatermarkOptions(message, position));
}

/**
 * @param {string}  props.url              — the document to open. For a video this is the raw presigned
 *                                           object; for everything else a PDF.
 * @param {string}  props.fileExtension    — picks the renderer: mp4, ogg and webm (any case) open in the
 *                                           video player, anything else in the PDF loader.
 * @param {{ message: string, position: 'left'|'top'|'bottom'|'center' }|null} [props.watermarkOptions]
 *                                         — drawn over a video once it has loaded. Never drawn over a
 *                                           PDF: that watermark is already burned in.
 */
export function PDFtron(props) {
  const { url, fileExtension, watermarkOptions } = props;
  const viewer = useRef(null);
  const intl = useIntl();
  const dispatch = useDispatch();
  const [viewerInstance, setViewerInstance] = useState(null);
  const [videoInstance, setVideoInstance] = useState(null);
  const isMountedRef = useRef(true);
  const instanceRef = useRef(null);
  const startedRef = useRef(false);
  // setCustomHeaderForVideo renames the header items' dataElements, so a second run cannot find
  // them and throws — it must run once per viewer instance, however often the video effect fires.
  const videoHeaderAppliedRef = useRef(false);
  // The url currently loaded / being loaded into the video viewer, so a re-fired effect does not
  // call loadVideo again for the same video (which interrupts the stream mid-load).
  const loadedVideoUrlRef = useRef(null);
  const loadingVideoUrlRef = useRef(null);
  const videoLoadGenerationRef = useRef(0);
  // The watermark a load that finishes later should draw: it can arrive while the video is still
  // loading, and the load's own closure would hold the options from when it started.
  const watermarkOptionsRef = useRef(watermarkOptions);

  const setCustomHeaderForVideo = useCallback((instance) => {
    const defaultHeader = instance.UI.getModularHeader('default-top-header');
    const headerItems = defaultHeader.getItems();

    const groupedLeftHeaderButtons = headerItems.find((item) => item.dataElement === 'groupedLeftHeaderButtons-custom');

    groupedLeftHeaderButtons.items.forEach((item) => {
      item.dataElement = `${item.dataElement}-video`; // Set custom data element name to avoid warnings
    });

    const ribbonGroup = headerItems.find((item) => item.dataElement === 'default-ribbon-group-custom');
    if (ribbonGroup) {
      ribbonGroup.setStyle({ flexGrow: 1 });
      ribbonGroup.dataElement = `${ribbonGroup.dataElement}-video`; // Set custom data element name to avoid warnings
      ribbonGroup.items = [
        {
          ...headerItems[1].items[0],
          dataElement: `${headerItems[1].items[0].dataElement}-video`, // Set custom data element name to avoid warnings
        },
        {
          ...headerItems[1].items[1],
          dataElement: `${headerItems[1].items[1].dataElement}-video`, // Set custom data element name to avoid warnings
        },
      ];
    }

    const settingsButton = new instance.UI.Components.CustomButton({
      dataElement: 'custom-settings-button-video',
      title: 'Show Settings',
      onClick: () => instance.UI.openElements(['settingsModal']),
      img: settingsIcon,
    });

    const fullScreenButton = new instance.UI.Components.CustomButton({
      dataElement: 'custom-full-screen-button-video',
      title: 'Full screen',
      onClick: instance.UI.toggleFullScreen,
      img: fullScreenIcon,
    });

    const newHeaderItems = [
      {
        ...groupedLeftHeaderButtons,

        dataElement: `${groupedLeftHeaderButtons.dataElement}-video`, // Set custom data element name to avoid warnings
        style: {
          flexGrow: 1,
        },
        items: [
          groupedLeftHeaderButtons.items[4], // This is the Zoom Control
          fullScreenButton,
          groupedLeftHeaderButtons.items[6], // This is a divider
        ],
      },
      settingsButton,
    ];

    defaultHeader.setItems(newHeaderItems);
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    const cleanup = () => {
      isMountedRef.current = false;
      const instance = instanceRef.current;
      instanceRef.current = null;
      if (instance && instance.UI && typeof instance.UI.dispose === 'function') {
        try {
          instance.UI.dispose();
        } catch (disposeErr) {
          console.log('WebViewer dispose error', disposeErr);
        }
      }
    };

    if (startedRef.current) {
      return cleanup;
    }
    startedRef.current = true;

    const loadWebViewer = async () => {
      let viewerInitialized = false;
      try {
        WebViewer.Iframe(
          {
            path: PDFTRON_LIB_PATH,
            disabledElements: [...DEFAULT_DISABLED_ELEMENTS],
            useDownloader: true,
            licenseKey: PDFTRON_WEBVIWER_LICENSE,
          },
          viewer.current
        )
          .then(async (instance) => {
            instanceRef.current = instance;
            if (!isMountedRef.current) {
              if (instance.UI && typeof instance.UI.dispose === 'function') instance.UI.dispose();
              return;
            }
            setViewerInstance(instance);
            viewerInitialized = true;
            instance.UI.setToolbarGroup('toolbarGroup-View');
            instance.UI.disableFeatures([
              instance.UI.Feature.ThumbnailMerging,
              instance.UI.Feature.ThumbnailReordering,
              instance.UI.Feature.MultipleViewerMerging,
            ]);

            // Removes page manipulation options when right-clicking on thumbnails
            const pageRotationControls = instance.UI.pageManipulationOverlay.getItems()[2];
            instance.UI.pageManipulationOverlay.update([pageRotationControls]);
            instance.UI.multiPageManipulationControls.update([]);

            // Set the top header style to match the height of the document details header
            const topHeader = instance.UI.getModularHeader('default-top-header');
            topHeader.setStyle({ paddingTop: 6, paddingBottom: 6, borderWidth: 1 });

            // WORKAROUND: webviewer-video 4.36.0 (and every later release) calls UI.setHeaderItems
            // when it renders its controls, but WebViewer 12 removed that legacy API. The call
            // throws inside the plugin's own React tree, nothing catches it, and the app-wide
            // ErrorBoundary replaces the page. A no-op is safe: the plugin only uses it to add its
            // "Video viewer options" button and redaction tools, and our video header is set
            // separately by setCustomHeaderForVideo. Mirrors the UI workspace (Pdftron.tsx).
            if (!instance.UI.setHeaderItems) {
              instance.UI.setHeaderItems = () => {};
            }

            let videoInst = null;
            try {
              videoInst = await initializeVideoViewer(instance, {
                license: PDFTRON_WEBVIWER_VIDEO_LICENSE,
                renderControls: true,
                showTooltipPreview: true,
                showFrames: false,
                libPath: PDFTRON_LIB_PATH,
              });
            } catch (videoInitErr) {
              console.log('Video viewer init failed (expected for non-video documents):', videoInitErr);
            }
            setVideoInstance(videoInst);

            instance.UI.addEventListener('loaderror', (err) => {
              console.log('PDFTron load error: ', err);
              if (!isMountedRef.current) return;

              dispatch(
                displayToast(
                  intl.formatMessage(
                    {
                      id: 'documentPage.documentLoadError',
                    },
                    {
                      a: (chunks) => (
                        <a
                          href="https://support.zenqms.com/hc/en-us/articles/360010581394-File-types-and-formats-allowed-to-be-uploaded-in-ZenQMS"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {chunks}
                        </a>
                      ),
                    }
                  )
                )
              );

              instance.UI.displayErrorMessage(intl.formatMessage({ id: 'documentPage.vebViewerDocumentLoadError' }));
            });

            const defaultHeader = instance.UI.getModularHeader('default-top-header');
            const headerItems = defaultHeader.getItems();

            const groupedLeftHeaderButtons = headerItems.find(
              (item) => item.dataElement === 'groupedLeftHeaderButtons'
            );

            if (!groupedLeftHeaderButtons) return;
            groupedLeftHeaderButtons.items.forEach((item) => {
              item.dataElement = `${item.dataElement}-custom`; // Set custom data element name to avoid warnings
            });

            const settingsButton = new instance.UI.Components.CustomButton({
              dataElement: 'custom-settings-button',
              title: 'Show Settings',
              onClick: () => instance.UI.openElements(['settingsModal']),
              img: settingsIcon,
            });

            const fullScreenButton = new instance.UI.Components.CustomButton({
              dataElement: 'custom-full-screen-button',
              title: 'Full screen',
              onClick: instance.UI.toggleFullScreen,
              img: fullScreenIcon,
            });

            const newHeaderItems = [
              {
                ...groupedLeftHeaderButtons,

                dataElement: `${groupedLeftHeaderButtons.dataElement}-custom`, // Set custom data element name to avoid warnings
                style: {
                  flexGrow: 1,
                },
                items: [
                  groupedLeftHeaderButtons.items[2], // This is the left panel button
                  groupedLeftHeaderButtons.items[1], // This is a divider
                  groupedLeftHeaderButtons.items[3], // This is the View Control
                  groupedLeftHeaderButtons.items[4], // This is a divider
                  groupedLeftHeaderButtons.items[5], // This is the Zoom Control
                  fullScreenButton,
                  groupedLeftHeaderButtons.items[6], // This is a divider
                ],
              },

              {
                ...headerItems[3],

                dataElement: `${headerItems[3].dataElement}-custom`, // Set custom data element name to avoid warnings
              },
              settingsButton,
            ];

            defaultHeader.setItems(newHeaderItems);
          })
          .catch((err) => {
            console.log('WebViewer Initialize error');
            console.log(err);

            if (isMountedRef.current && !viewerInitialized) {
              dispatch(displayToast(intl.formatMessage({ id: 'documentPage.webViewerLoadError' })));
            }
          });
      } catch (err) {
        console.log('WebViewer Initialize error');
        console.log(err);
        if (isMountedRef.current) {
          dispatch(displayToast(intl.formatMessage({ id: 'documentPage.webViewerLoadError' })));
        }
      }
    };

    loadWebViewer();

    return cleanup;
  }, []);

  // Configure the viewerInstance once it was initialized
  useEffect(() => {
    if (!viewerInstance) return;

    const {
      Core: { Tools, annotationManager, documentViewer, Annotations },
      UI,
    } = viewerInstance;

    // Disable text selection on all tools that support it
    const allTools = Object.values(documentViewer.getToolModeMap());
    for (const tool of allTools) {
      if (typeof tool.disableTextSelection === 'function') {
        tool.disableTextSelection();
      }
    }
    annotationManager.enableReadOnlyMode();
    UI.contextMenuPopup.update([]);
    UI.disableFeatures([UI.Feature.Print]);

    // Disable form inputs from the original pdf document
    documentViewer.addEventListener('annotationsLoaded', () => {
      const annots = annotationManager.getAnnotationsList();

      // find the widget annotations
      const widgetAnnots = annots.filter((a) => a instanceof Annotations.WidgetAnnotation);

      // Set the read only for each widget annotation
      widgetAnnots.forEach((a) => {
        // eslint-disable-next-line dot-notation
        a['fieldFlags'].set('ReadOnly', true);
      });
    });

    // Workaround for Checkboxes not being ticked issue
    annotationManager.addEventListener('annotationChanged', (annotations, action) => {
      if (action === 'add') {
        annotations.forEach((annotation) => {
          if (annotation instanceof Annotations.CheckButtonWidgetAnnotation) {
            if (annotation.defaultValue === 'On') {
              annotation.value = 'On';
              annotationManager.redrawAnnotation(annotation);
            }
          }
        });
      }
    });
  });

  // Load the PDF document, if it is the case
  useEffect(() => {
    // Quit if not everything is properly initialized
    if (![viewerInstance, url, fileExtension].every(Boolean)) {
      return;
    }

    // For video files don't try to load the PDF files
    if (VIDEO_FORMATS.includes(fileExtension.toLocaleLowerCase())) {
      return;
    }

    const loadPdfDocument = async () => {
      try {
        viewerInstance.UI.loadDocument(url, { extension: 'pdf' });
      } catch (err) {
        console.log('PDF Document Load error');
        console.log(err);
        if (isMountedRef.current) {
          dispatch(displayToast(intl.formatMessage({ id: 'documentPage.webViewerLoadError' })));
        }
      }
      return true;
    };

    loadPdfDocument();
  }, [viewerInstance, url, fileExtension]);

  // Declared before the video effects on purpose: effects run in declaration order, so the ref is
  // current by the time the video effect below (re)starts a load in the same commit.
  useEffect(() => {
    watermarkOptionsRef.current = watermarkOptions;
  }, [watermarkOptions]);

  // Load the video file, if it is the case
  useEffect(() => {
    // Forgets every video load: advancing the generation makes any in-flight load stale (it can no
    // longer mark itself loaded, draw its watermark or report an error), and clearing both urls
    // means only the current request can satisfy the "already loaded/loading" check below.
    const invalidateVideoLoads = () => {
      videoLoadGenerationRef.current += 1;
      loadingVideoUrlRef.current = null;
      loadedVideoUrlRef.current = null;
    };

    // Quit if not everything is properly initialized. The requested document is not (or no longer)
    // a video we can show, so whatever video was loaded or loading is no longer current.
    if (![viewerInstance, url, fileExtension, videoInstance].every(Boolean)) {
      invalidateVideoLoads();
      return;
    }

    // For non-video files don't try to load the video editor. The PDF effect replaces the viewer
    // content, so a previously loaded video must not count as loaded when we come back to it.
    if (!VIDEO_FORMATS.includes(fileExtension.toLocaleLowerCase())) {
      invalidateVideoLoads();
      return;
    }

    if (!videoHeaderAppliedRef.current) {
      setCustomHeaderForVideo(viewerInstance);
      videoHeaderAppliedRef.current = true;
    }

    // Skip when this video is already loaded or loading: a second loadVideo for the same url
    // interrupts the stream mid-load.
    if (loadedVideoUrlRef.current === url || loadingVideoUrlRef.current === url) {
      return;
    }

    // A different video: the previous one (loaded or still pending) is superseded.
    invalidateVideoLoads();

    const loadVideoDocument = async () => {
      const generation = videoLoadGenerationRef.current;
      loadingVideoUrlRef.current = url;
      try {
        const { loadVideo } = videoInstance;
        // Awaited so a failed load is handled here rather than surfacing as an unhandled rejection.
        // fileName: without it the plugin warns and falls back to a default name.
        // The presigned url goes in exactly as signed: a query parameter added to it is not covered
        // by a SigV4 signature, and storage then answers 403.
        await loadVideo(url, { fileName: `video.${fileExtension.toLocaleLowerCase()}`, fps: VIDEO_FPS });
        // Also stop when the viewer was disposed meanwhile (the preview was closed): unmounting
        // does not advance the generation, and drawing on a disposed viewer throws.
        if (!isMountedRef.current || videoLoadGenerationRef.current !== generation) return;
        loadedVideoUrlRef.current = url;

        // Only now that the stream is established: drawing while loadVideo is still creating it
        // caused a Shaka error in nova. The latest options are read from the ref because they may
        // have arrived after this load started.
        drawVideoWatermark(viewerInstance, watermarkOptionsRef.current);
      } catch (err) {
        // A newer load superseded this one; its outcome is the one that matters.
        if (videoLoadGenerationRef.current !== generation) return;
        console.log('PDF Video Load error');
        console.log(err);
        if (isMountedRef.current) {
          dispatch(displayToast(intl.formatMessage({ id: 'documentPage.webViewerLoadError' })));
        }
      } finally {
        if (videoLoadGenerationRef.current === generation) {
          loadingVideoUrlRef.current = null;
        }
      }
    };

    loadVideoDocument();
  }, [viewerInstance, videoInstance, url, fileExtension]);

  // Draw watermark options that arrive after the video has already loaded (e.g. the document
  // metadata resolves later). The first draw is made by the load itself, once loadVideo resolves;
  // this effect must not draw while a load is in flight, so it only acts on a loaded video.
  useEffect(() => {
    // Quit if not everything is properly initialized
    if (![viewerInstance, url, fileExtension, videoInstance, watermarkOptions].every(Boolean)) {
      return;
    }

    // Never over a PDF: its watermark is already burned in.
    if (!VIDEO_FORMATS.includes(fileExtension.toLocaleLowerCase())) {
      return;
    }

    // Still loading (or failed, or superseded): the load draws it if and when it succeeds.
    if (loadedVideoUrlRef.current !== url) {
      return;
    }

    drawVideoWatermark(viewerInstance, watermarkOptions);
  }, [viewerInstance, url, fileExtension, videoInstance, watermarkOptions]);

  return (
    <div className="PdfTron" style={{ width: '100%', height: '100%' }}>
      <div className="webviewer" ref={viewer} style={{ height: '100%', width: '100%' }}></div>
    </div>
  );
}
